import { CodeLoginVerificationDto, CurrentServiceAccountResponseDto, InitiateCodeLoginDto, ScramClientFirstDto, ScramServerFirstDto, SsoAccountMergeFinalDto, SsoUserLoginResponseDto, UserLoginServerFinalDto, UserLoginServerFirstDto, UserLoginSsoStateResponseDto, UserResponseDto, } from '@lucidlink/dto/web-service-api';
import { arrayToAesKey, base64ToArray, deriveAesSecretKey, } from '@lucidlink/ts-crypto/crypto';
import { parserFromDto } from '#fetcher';
import { createAccessTokenFetcher, decryptAccountKey, prepareClientFinal, prepareClientFirst, verifySsoOperation, } from '#sdk/common';
import { assertIsUser } from '#session/session-context-data';
import { remapUserDto, } from '#types/sdk/data-objects';
import { getServiceAccountWebServiceToken, parseServiceAccountToken, } from '#utils';
import { isUserAccountDetails } from '#utils/account-details';
import { acceptSsoInvitation } from '../common/invitation';
import { RequiredCryptoError } from '../workspace-client/crypto';
import { prepareRegisterUserDto } from './register-user/prepare-register-user-dto';
import { prepareSsoAccountMergeDto } from './register-user/prepare-sso-account-merge-dto';
import { prepareSsoRegisterUserDto } from './register-user/prepare-sso-register-user-dto';
export const createAuthenticationClient = ({ getSessionManager, getUnauthenticatedFetcher, getGlobalFetcher, logoutWorkspaces, }) => {
    return new AuthenticationClientImpl(getUnauthenticatedFetcher, getSessionManager, getGlobalFetcher, logoutWorkspaces);
};
class AuthenticationClientImpl {
    getUnauthenticatedFetcher;
    getSessionManager;
    getGlobalFetcher;
    logoutWorkspaces;
    constructor(getUnauthenticatedFetcher, getSessionManager, getGlobalFetcher, logoutWorkspaces) {
        this.getUnauthenticatedFetcher = getUnauthenticatedFetcher;
        this.getSessionManager = getSessionManager;
        this.getGlobalFetcher = getGlobalFetcher;
        this.logoutWorkspaces = logoutWorkspaces;
    }
    async registerUser(params) {
        const registerUserDto = await prepareRegisterUserDto(params);
        const result = await this.getUnauthenticatedFetcher().post('users', {
            body: registerUserDto,
            parser: parserFromDto(UserResponseDto),
        });
        return remapUserDto(result);
    }
    async login(params) {
        const clientFirstMessage = prepareClientFirst(params.email);
        const serverFirstMessage = await this.getUnauthenticatedFetcher().post(['user-login', 'first'], {
            body: clientFirstMessage,
            parser: parserFromDto(UserLoginServerFirstDto),
        });
        const clientFinalMessage = await prepareClientFinal(clientFirstMessage, serverFirstMessage, params.password);
        const serverFinalMessage = await this.getUnauthenticatedFetcher().post(['user-login', 'final'], {
            body: clientFinalMessage,
            parser: parserFromDto(UserLoginServerFinalDto),
        });
        await this.getSessionManager().globalLoginByPassword(serverFinalMessage, params);
    }
    async serviceAccountLogin(serviceAccountToken) {
        const token = parseServiceAccountToken(serviceAccountToken);
        const serviceAccount = await this.getUnauthenticatedFetcher().get(['service-accounts', 'me'], {
            parser: parserFromDto(CurrentServiceAccountResponseDto),
            headers: {
                Authorization: `Bearer ${getServiceAccountWebServiceToken(token)}`,
            },
        });
        await this.getSessionManager().loginByServiceAccount(serviceAccount, token);
        return serviceAccount;
    }
    async initiateCodeLogin(email) {
        await this.getUnauthenticatedFetcher().post(['user-login', 'code-first'], {
            body: InitiateCodeLoginDto.create({ email }),
        });
    }
    async verifyCodeLogin(email, verificationCode) {
        const codeLoginDto = await this.getUnauthenticatedFetcher().post(['user-login', 'code-final'], {
            body: CodeLoginVerificationDto.create({
                email,
                verificationCode,
            }),
        });
        await this.getSessionManager().globalLoginByVerificationCode({
            ...codeLoginDto,
            email,
        });
    }
    restoreLogin() {
        return this.getSessionManager().tryRestoreLogin();
    }
    async logout() {
        await this.logoutWorkspaces();
        return this.getSessionManager().logout();
    }
    async verifyDomainSecret(params) {
        await this.prepareForSsoOperation(params);
    }
    async ssoRegisterUser(params) {
        const { ssoFetcher } = await this.prepareForSsoOperation(params);
        const registerUserDto = await prepareSsoRegisterUserDto(params);
        const result = await ssoFetcher.post(['users', 'sso'], {
            body: registerUserDto,
            parser: parserFromDto(UserResponseDto),
            queryParams: { domainCrypto: true },
        });
        return remapUserDto(result);
    }
    async ssoAccountMerge(params) {
        const { ssoFetcher } = await this.prepareForSsoOperation(params);
        const clientFirstMessage = ScramClientFirstDto.create(prepareClientFirst(params.email));
        const serverFirstMessage = await ssoFetcher.post(['users', 'sso-account-merge', 'password-verification', 'first'], {
            body: clientFirstMessage,
            parser: parserFromDto(ScramServerFirstDto),
        });
        const clientFinalMessage = await prepareClientFinal(clientFirstMessage, serverFirstMessage, params.password);
        const serverFinalMessage = await ssoFetcher.post(['users', 'sso-account-merge', 'password-verification', 'final'], {
            body: clientFinalMessage,
            parser: parserFromDto(SsoAccountMergeFinalDto),
        });
        const ssoAccountMergeFetcher = this.createAccessTokenFetcher(serverFinalMessage.accessToken, {
            path: ['users', 'sso-account-merge', 'merge'],
        });
        const ssoAccountMergeDto = await prepareSsoAccountMergeDto(params, serverFinalMessage);
        const result = await ssoAccountMergeFetcher.post([], {
            body: ssoAccountMergeDto,
            parser: parserFromDto(UserResponseDto),
            queryParams: { domainCrypto: true },
        });
        return remapUserDto(result);
    }
    async ssoLogin(params) {
        const domainSecret = await this.getCryptoContext(params);
        const { ssoFetcher, ssoInvitation } = await this.prepareForSsoOperation({ accessToken: params.accessToken, domainSecret });
        const ssoUserLogin = await ssoFetcher.post(['user-login', 'sso'], {
            parser: parserFromDto(SsoUserLoginResponseDto),
            ...(params.skipGlobalLogin && { queryParams: { ephemeral: true } }),
        });
        await this.ensureAcceptedSsoInvitation(domainSecret, ssoUserLogin, ssoInvitation, ssoFetcher);
        if (params.skipGlobalLogin) {
            return;
        }
        if (typeof domainSecret !== 'string') {
            throw new RequiredCryptoError();
        }
        await this.getSessionManager().globalLoginBySsoSecret(ssoUserLogin, {
            ...params,
            domainSecret: domainSecret,
        });
    }
    async verifyUserEmail(params) {
        const user = await this.getUnauthenticatedFetcher().put('email-verification', {
            body: params,
            parser: parserFromDto(UserResponseDto),
        });
        return remapUserDto(user);
    }
    async resendVerificationEmail() {
        return this.getGlobalFetcher().post('email-verification');
    }
    async getUserSsoState(email, accessToken) {
        const ssoFetcher = this.createAccessTokenFetcher(accessToken, {
            path: ['user-login'],
        });
        const ssoStateRequest = await ssoFetcher.get([encodeURIComponent(email), 'sso-state'], {
            parser: parserFromDto(UserLoginSsoStateResponseDto),
        });
        return ssoStateRequest;
    }
    async verifyTotp(totpToken) {
        await this.getGlobalFetcher().post(['user-login', 'mfa-totp'], {
            body: {
                totpToken,
            },
        });
        // NOTE: Flows which update a property in the global token MUST force refresh of token to get the updated token
        await this.getSessionManager().getFreshGlobalToken(true);
    }
    async initiateMfaDisable() {
        await this.getGlobalFetcher().put([
            'users',
            'me',
            'mfa',
            'recovery-init',
        ]);
    }
    async verifyMfaDisableEmail(verifyEmailParams) {
        await this.getGlobalFetcher().put(['users', 'me', 'mfa', 'recovery-email-verification'], {
            body: { verificationCode: verifyEmailParams.code },
        });
        await this.getSessionManager().getFreshGlobalToken(true);
    }
    async verifyRecoveryCode(backupCode) {
        const accountDetails = await this.getSessionManager().getAccountDetails();
        assertIsUser(accountDetails);
        const clientFirst = prepareClientFirst(accountDetails.email);
        const serverFirst = await this.getGlobalFetcher().put(['users', 'me', 'mfa', 'recovery-scram-first'], {
            body: clientFirst,
            parser: parserFromDto(ScramServerFirstDto),
        });
        const clientFinal = await prepareClientFinal(clientFirst, serverFirst, backupCode);
        await this.getGlobalFetcher().put(['users', 'me', 'mfa', 'recovery-scram-final'], {
            body: clientFinal,
        });
        // NOTE: Flows which update a property in the global token MUST force refresh of token to get the updated token
        await this.getSessionManager().getFreshGlobalToken(true);
    }
    // NOTE: the `domainSecret` here is a union of `CryptoKey | string` due to specifics in the ssoLogin() flow
    // most notably when we don't skip the global login inside the ssoLogin() we need the `domainSecret` to be string
    // since that's the only way to create both the encryption key and the wrapping key from the same secret
    async prepareForSsoOperation(params) {
        return verifySsoOperation({
            unauthenticatedFetcher: this.getUnauthenticatedFetcher(),
            accessToken: params.accessToken,
            domainKey: params.domainSecret,
        });
    }
    async ensureAcceptedSsoInvitation(domainSecret, ssoUserLogin, ssoInvitation, ssoFetcher) {
        const userKey = await this.getUserKey({
            userEncryptedKey: ssoUserLogin.userEncryptedKey,
            userEncryptedKeySalt: ssoUserLogin.userEncryptedKeySalt,
            domainSecret,
        });
        const domainKey = typeof domainSecret === 'string'
            ? await arrayToAesKey(base64ToArray(domainSecret))
            : domainSecret;
        await acceptSsoInvitation({
            userKey,
            domainKey,
            ssoInvitation,
            ssoFetcher,
        });
    }
    createAccessTokenFetcher = (accessToken, args) => {
        return createAccessTokenFetcher(this.getUnauthenticatedFetcher(), accessToken, args);
    };
    getUserKey = async (params) => {
        if (typeof params.domainSecret === 'string') {
            const aesSecretKey = await deriveAesSecretKey(params.domainSecret, params.userEncryptedKeySalt);
            return decryptAccountKey(params.userEncryptedKey, aesSecretKey);
        }
        const accountDetails = await this.getSessionManager().getAccountDetails();
        if (!isUserAccountDetails(accountDetails)) {
            throw new RequiredCryptoError();
        }
        return decryptAccountKey(params.userEncryptedKey, accountDetails.wrappingKey);
    };
    async getCryptoContext(params) {
        const storedDomainKey = await this.getSessionManager().tryRestoreDomainKey();
        if (!params.domainSecret && !storedDomainKey) {
            throw new RequiredCryptoError();
        }
        return params.domainSecret ?? storedDomainKey;
    }
}
